import React from "react";
import { Link } from "react-router-dom";
import LandingLayout from "../components/LandingLayout";
import { useRedirectIfLoggedIn } from "../hooks/useRedirectIfLoggedIn";
import LandingHero from "../components/landing/LandingHero";
import ProblemsSection from "../components/landing/ProblemsSection";
import SolutionGrid from "../components/landing/SolutionGrid";
import HowItWorks from "../components/landing/HowItWorks";
import EarlyAccessCTA from "../components/landing/EarlyAccessCTA";
import FinalCTASection from "../components/landing/FinalCTASection";

const problems = [
    {
        title: "Presupuestar lleva demasiado tiempo",
        description: "Cada proyecto nuevo son horas de cálculos: medir, estimar materiales, calcular mano de obra. Y muchos presupuestos no se cierran.",
    },
    {
        title: "El cliente llega perdido",
        description: "El 75% no tiene idea clara de lo que quiere: saben qué necesitan pero no cómo diseñarlo, medidas, permisos ni logística.",
    },
    {
        title: "Competir sin diferenciarte",
        description: "Sin una propuesta profesional y rápida, acabas compitiendo solo en precio contra constructores que cobran menos.",
    },
];

const solutions = [
    {
        icon: "📷",
        title: "Sube fotos, recibe presupuesto",
        description: "La IA detecta actuaciones necesarias a partir de las fotos: suelos, azulejos, fontanería, electricidad, pintura...",
    },
    {
        icon: "📊",
        title: "Desglose por partidas",
        description: "Materiales, mano de obra, unidades, metros cuadrados. Todo calculado y listo para ajustar.",
    },
    {
        icon: "💬",
        title: "Pregunta lo que necesites",
        description: "\"Dime los metros cuadrados de azulejos para la cocina de la última propiedad.\" La IA tiene contexto completo.",
    },
    {
        icon: "📄",
        title: "Presupuesto profesional",
        description: "PDF exportable con tu marca. Envíalo al cliente directamente. Profesionalidad sin oficina técnica.",
    },
];

const steps = [
    {
        title: "Sube las fotos",
        description: "Haz fotos en la visita o súbelas desde el anuncio. La IA analiza el estado de cada estancia.",
    },
    {
        title: "Revisa y ajusta",
        description: "El sistema genera un presupuesto desglosado. Ajusta partidas, precios o unidades según tu criterio.",
    },
    {
        title: "Envía al cliente",
        description: "Exporta en PDF con tu marca y preséntalo. Profesional, rápido y sin oficina técnica propia.",
    },
];

const LandingConstructor = () => {
    useRedirectIfLoggedIn("/analizarpropiedad/nueva");
    return (
        <LandingLayout>
            <div className="max-w-5xl mx-auto px-6 pt-4">
                <Link to="/" className="text-sm text-gray-500 hover:text-brand-blue transition-colors">
                    ← Todos los perfiles
                </Link>
            </div>

            <LandingHero
                badge="Para constructores y reformistas"
                title="Haz presupuestos"
                highlight="sin oficina técnica"
                subtitle="Genera presupuestos detallados a partir de fotos con inteligencia artificial. Lo que antes te costaba 8 horas, ahora te lleva 1. Ideal para autónomos y microempresas."
                ctaText="Generar mi primer presupuesto"
                bgClass="bg-gradient-to-br from-green-50 to-green-100"
                highlightClass="text-green-700"
            />

            <section className="py-12 bg-white border-b border-gray-100">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="grid grid-cols-3 gap-8 text-center">
                        {[
                            { number: "8x", label: "Más rápido que presupuestar a mano" },
                            { number: "1h", label: "Un presupuesto completo con IA" },
                            { number: "0€", label: "Coste de oficina técnica" },
                        ].map((item, i) => (
                            <div key={i}>
                                <div className="text-4xl font-extrabold text-brand-blue mb-2">{item.number}</div>
                                <div className="text-sm text-gray-500">{item.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <ProblemsSection
                title="Problemas que conoces bien"
                problems={problems}
            />

            <SolutionGrid
                title="Tu oficina técnica en la nube"
                subtitle="La IA analiza las fotos de la propiedad y genera un presupuesto desglosado por partidas."
                solutions={solutions}
            />

            <HowItWorks steps={steps} />

            <section className="py-16 bg-gray-50">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-brand-blue mb-8 text-center">Pensado para ti</h2>
                    <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Autónomos y microempresas de reforma</h3>
                        <p className="text-gray-500">
                            Si trabajas con 1-2 operarios y no tienes equipo técnico propio, Reformista te da la capacidad de presupuestar como una empresa grande. Sin contratar a nadie.
                        </p>
                    </div>
                </div>
            </section>

            <EarlyAccessCTA />

            <FinalCTASection
                title="De 8 horas a 1. Sin oficina técnica."
                text="Regístrate, sube las fotos de tu próximo proyecto y genera un presupuesto profesional en minutos."
                ctaText="Acceder a Reformista"
                rolNombre="constructor"
            />
        </LandingLayout>
    );
};

export default LandingConstructor;
